import type { Action, PayloadAction } from '@reduxjs/toolkit';
import { analytics, type ShopInsuranceQuoteAddedToPlanProperties } from '@shared/posthog';
import type { AppState } from '../model/store';

const SHOP_INSURANCE_QUOTE_ADDED_META_KEY = 'shopInsuranceQuoteAddedToPlan';
const capturedIllustrationRequestIds = new Set<string>();

type ShopInsuranceQuoteAddedMeta = {
  [SHOP_INSURANCE_QUOTE_ADDED_META_KEY]: ShopInsuranceQuoteAddedToPlanProperties;
};

type ActionWithOptionalMeta = Action<string> & {
  meta?: Partial<ShopInsuranceQuoteAddedMeta>;
  payload?: unknown;
};

export function withShopInsuranceQuoteAddedToPlanEvent<TPayload, TType extends string>(
  action: PayloadAction<TPayload, TType>,
  properties: ShopInsuranceQuoteAddedToPlanProperties,
): PayloadAction<TPayload, TType, ShopInsuranceQuoteAddedMeta> {
  return {
    ...action,
    meta: {
      [SHOP_INSURANCE_QUOTE_ADDED_META_KEY]: properties,
    },
  };
}

function captureQuoteAddedToPlan(action: Action<string>) {
  const properties = (action as ActionWithOptionalMeta).meta?.[SHOP_INSURANCE_QUOTE_ADDED_META_KEY];
  if (properties) {
    analytics.shopInsurance.quoteAddedToPlan(properties);
  }
}

function captureIllustrationGenerated(
  action: Action<string>,
  requestFnName: string | undefined,
  response: unknown,
  state: AppState,
) {
  if (
    requestFnName !== 'fetchIllustrationStatusByRequestId' ||
    typeof response !== 'object' ||
    response === null
  ) {
    return;
  }

  const responseRecord = response as Record<string, unknown>;
  const requestPayload = (action as ActionWithOptionalMeta).payload;
  const requestId =
    typeof requestPayload === 'object' &&
    requestPayload !== null &&
    'requestId' in requestPayload &&
    typeof requestPayload.requestId === 'string'
      ? requestPayload.requestId
      : null;

  if (
    !requestId ||
    responseRecord.requestId !== requestId ||
    responseRecord.status !== 'completed' ||
    typeof responseRecord.pdfUrl !== 'string' ||
    typeof responseRecord.clientId !== 'string' ||
    typeof responseRecord.insuranceQuoteId !== 'string' ||
    capturedIllustrationRequestIds.has(requestId)
  ) {
    return;
  }

  const activeRequest = Object.values(state.illustration.activeRequests).find(
    request => request.requestId === requestId,
  );
  if (!activeRequest || activeRequest.clientPolicyApplicationId) return;

  capturedIllustrationRequestIds.add(requestId);
  analytics.shopInsurance.illustrationGenerated({
    clientId: responseRecord.clientId,
    quoteId: responseRecord.insuranceQuoteId,
  });
}

export function capturePosthogApiSuccess({
  action,
  requestFnName,
  response,
  state,
}: {
  action: Action<string>;
  requestFnName: string | undefined;
  response: unknown;
  state: AppState;
}) {
  captureQuoteAddedToPlan(action);
  captureIllustrationGenerated(action, requestFnName, response, state);
}
